/**
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/participant';

import PagerModel from '@netx/core-app/models/pager';
import SorterModel from '@netx/core-app/models/sorter';

export default class Participants extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} methods       - Collection methods
	 * @property {method} methods.read  - Fetch participants
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return [
					'getParticipants',
					this.resourceName,
					this.resourceId,
					this.filterStr,
					this.sorter.getSortDTO(),
					this.pager.getPageDTO(),
				];
			},
		};
	}

	/**
	 * @property {object}   parsers       - Collection parsers
	 * @property {function} parsers.read  - Parse participants
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} participants Participants
			 * @param {object} opts Request options
			 */
			read(participants, opts) {
				return _.map(participants, (participant) => {
					// We want to combine the type and id `<type>-<id>`
					// `all` has no id and is just "all"
					const uniqueId = _.compact(
						_.values(_.pick(participant, 'type', 'id')),
					).join('-');

					return _.extend(participant, {
						uniqueId: uniqueId,
					});
				});
			},
		};
	}

	/**
	 * @override
	 * @param {array} models An array of models or model attributes
	 * @param {object} opts Model options
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * @memberof module:asset/collections/participants
		 * @alias resourceId
		 * @type {number}
		 */
		this.resourceId = opts.resourceId;

		/**
		 * @memberof module:asset/collections/participants
		 * @alias resourceName
		 * @type {string}
		 */
		this.resourceName = opts.resourceName;

		/**
		 * Paging model
		 * @memberof module:asset/collections/participants
		 * @alias pager
		 * @type {module:app/models/pager}
		 */
		this.pager = new PagerModel(
			{},
			{
				collection: this,
			},
		);

		/**
		 * Sorting model
		 * @memberof module:asset/collections/participants
		 * @alias sorter
		 * @type {module:app/models/sorter}
		 */
		this.sorter = new SorterModel(
			{
				field: opts.sortField || 'label',
				order: opts.sortOrder || 'desc',
			},
			{
				collection: this,
			},
		);

		// Hey, listen!
		this.listenTo(
			dispatcher,
			'permissions:participant:edit:success',
			(participant, isNew) => {
				if (
					participant.get('resourceName') !== this.resourceName ||
					participant.get('resourceId') !== this.resourceId ||
					this.get(participant.id)
				)
					return;

				this.add(participant);
			},
		);
	}

	/**
	 * @method
	 * @param {object} participant Participant object
	 * @param {object} opts Request options
	 */
	addParticipant(participant, opts) {
		const model = new this.model(
			_.extend(
				_.pick.apply(_, [participant].concat(_.keys(this.model.GetDefaults()))),
				{
					resourceId: this.resourceId,
					resourceName: this.resourceName,
				},
			),
		);

		return model.save().then(() => this.add(model, opts));
	}

	/**
	 * Filter by participant type
	 * @param {string} type Type (user/group/all)
	 * @return {module:permissions/collections/participants} Participants
	 */
	filterByType(type) {
		return new this.constructor(
			this.filter((model) => model.get('type') === type),
			{
				resourceName: this.resourceName,
				resourceId: this.resourceId,
			},
		);
	}

	/**
	 * Get participant by type (and id)
	 * @param {string} type Type
	 * @param {number} id ID
	 * @return {module:permissions/models/participant} Participant
	 */
	getParticipant(type, id) {
		if (type === 'all') {
			return this.findWhere({type: type});
		}
		return this.findWhere({type: type, id: id});
	}

	/**
	 * @method
	 * @param {number} id Participant id
	 */
	removeParticipant(id) {
		const model = this.get(id);

		if (!model) {
			return new $.Deferred().reject(
				new Error('Participant model with id `' + id + '` not found.'),
			);
		}

		return model.destroy({wait: true});
	}

	/** @type {string} */
	static get __name__() {
		return 'Participants';
	}
}
